import { captureException } from "@sentry/react"
import { Button, IconButton, Typography } from "@suraasa/placebo-ui"
import { useQuery } from "@tanstack/react-query"
import api from "api"
import { queries } from "api/queries"
import { PaymentMethodType } from "api/resources/payment/types"
import {
  IntervalType,
  MetaDataForPaidUser,
} from "api/resources/suraasaPlus/types"
import clsx from "clsx"
import AsyncBuilder from "components/AsyncBuilder"
import { format } from "date-fns"
import { getPurchaseDetails } from "features/Payment/CartlessSubscriptionFlow"
import {
  getLogoSrc,
  SubscriptionSlugs,
} from "features/Payment/CartlessSubscriptionFlow/helper"
import PaymentUnderProcess from "features/Payment/CartlessSubscriptionFlow/PaymentUnderProcess"
import { useSubscriptionPayment } from "features/Payment/CartlessSubscriptionFlow/useSubscriptionPayment"
import SuraasaPlusLogo, {
  LogoVariant,
} from "features/SuraasaPlus/components/SuraasaPlusLogo"
import { useSuraasaPlusContext } from "features/SuraasaPlus/context"
import { motion } from "framer-motion"
import { CheckCircle, Xmark } from "iconoir-react"
import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import {
  PAYMENT_UNDER_PROCESS_PAGE_PORTAL,
  SURAASA_PLUS_SUBSCRIPTION_PRODUCT_SLUG,
} from "utils/constants"
import { mapErrorsV2 } from "utils/helpers"
import useFormatPrice from "utils/hooks/useFormatPrice"

import lightOverlay from "../assets/light_layer.webp"
import darkOverlay from "../assets/top_dark_layer.webp"
import { DialogThemeMode } from "../utils"
import AdaptiveDialogAndSheet from "./AdaptiveDialogAndSheets"

type Props = {
  open: boolean
  handleClose: () => void
  mode?: DialogThemeMode
}

enum ChangeSteps {
  offers = "offers",
  success = "success",
  paymentInProgress = "paymentInProgress",
}

const modeStyles = {
  [DialogThemeMode.dark]: {
    body: "bg-[#0E1525]",
    header:
      "bg-[linear-gradient(180deg,#4C1278_0%,#082743_56.82%,#0E1525_100%)]",
    footer: "bg-[#0E1525]",
    selectedBg: "bg-[#272E3D]",
    successBg: "bg-[linear-gradient(180deg,#082743_0%,#0E1525_100%)]",
    checkCircle: "text-[#6DE0CB]",
    text: {
      plus: "#FFF7C3",
      current: "text-onSurface-400",
      primary: "text-white",
      secondary: "text-onSurface-400",
      planHeading: "text-white",
      planPrice: "text-white",
      highlight: "text-onSurface-200",
    },
    buttons: {
      x: "text-surface hover:bg-onSurface-200/20 active:bg-onSurface-300/40 active:text-onSurface-900",
      text: "text-white hover:bg-white/20 active:bg-white/30",
      primary: "bg-surface text-onSurface-900",
    },
  },
  [DialogThemeMode.light]: {
    body: "bg-white",
    header: "bg-white",
    footer: "bg-gradient-to-b from-white/0 via-white to-white",
    selectedBg: "bg-[#F1F5F9]",
    successBg: "bg-surface",
    checkCircle: "text-success-500",
    text: {
      plus: "#83806A",
      primary: "text-onSurface-900",
      current: "text-onSurface-500",
      secondary: "text-onSurface-500",
      planHeading: "text-onSurface-700",
      planPrice: "text-onSurface-900",
      highlight: "text-onSurface-800",
    },
    buttons: {
      x: "text-onSurface-700 hover:bg-gray-100 active:bg-gray-200",
      text: "text-onSurface-700 hover:bg-gray-100 active:bg-gray-200",
      primary: "bg-onSurface-900 text-white",
    },
  },
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
}

const SuccessStep = ({
  styles,
  validThroughDate,
  updatedPlan,
  handleClose,
}: {
  styles: any
  validThroughDate?: Date | string
  updatedPlan: string
  handleClose: () => void
}) => (
  <div className={styles.successBg}>
    <div className="flex justify-end p-2 pb-0 ">
      <IconButton
        className={clsx("rounded-lg", styles.buttons.x)}
        onClick={handleClose}
      >
        <Xmark className="!size-2.5" strokeWidth={2} />
      </IconButton>
    </div>
    <motion.div
      className="flex flex-col items-center p-3 pt-0"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 200, damping: 15 }}
      >
        <CheckCircle className={clsx("mb-3 size-7", styles.checkCircle)} />
      </motion.div>

      <motion.div variants={itemVariants}>
        <Typography
          variant="title3"
          className={clsx("mb-1", styles.text.primary)}
        >
          Membership Updated
        </Typography>
      </motion.div>

      <motion.div variants={itemVariants}>
        <Typography
          variant="body"
          className={clsx("max-w-[320px] text-center", styles.text.secondary)}
        >
          Your subscription has been updated to the{" "}
          <span className={styles.text.highlight}>{updatedPlan}</span>. It will
          be effective from the next billing cycle
          {validThroughDate && (
            <>
              {" "}
              on{" "}
              <span className={styles.text.highlight}>
                {format(new Date(validThroughDate), "d MMMM yyyy")}
              </span>
            </>
          )}
          .
        </Typography>
      </motion.div>
    </motion.div>
  </div>
)

const ChangeYourPlanDialog = ({
  open,
  handleClose,
  mode = DialogThemeMode.dark,
}: Props) => {
  const [step, setStep] = useState<ChangeSteps>(ChangeSteps.offers)
  const [isDialogMinimized, setIsDialogMinimized] = useState(false)

  const styles = modeStyles[mode]

  const productDetailsQuery = useQuery({
    queryKey: queries.suraasaPlus.productDetails({
      slug: SURAASA_PLUS_SUBSCRIPTION_PRODUCT_SLUG,
    }).queryKey,
    queryFn: () =>
      api.suraasaPlus.retrieveProductGroup({
        urlParams: { slug: SURAASA_PLUS_SUBSCRIPTION_PRODUCT_SLUG },
      }),
    onError: e => mapErrorsV2(e),
  })

  const { subscriptionDetails, subscriptionDetailsQuery } =
    useSuraasaPlusContext()
  const formatPrice = useFormatPrice()
  const currentPlanId = subscriptionDetails?.subscription.productId

  const products = productDetailsQuery.data?.products ?? []

  const currentPlan = products.find(p => p.id.toString() === currentPlanId)
  const targetedPlan = products.find(p => p.id.toString() !== currentPlanId)

  const isCurrentPlanMonthly =
    currentPlan?.subscriptionDetails?.intervalType === IntervalType.month

  const currencySymbol = targetedPlan?.price?.currency?.symbol ?? ""
  const price = targetedPlan?.price?.price ?? 0
  const mrp = targetedPlan?.price?.mrp ?? 0
  const savings = mrp - price

  const productSlug = subscriptionDetails?.product?.slug as SubscriptionSlugs

  const logoSrc = getLogoSrc(productSlug)

  const product = useQuery({
    enabled: !!targetedPlan?.slug,
    queryFn: () => {
      return api.products.getProduct({
        params: {
          fields: ["prices"],
        },
        urlParams: { productSlug: targetedPlan?.slug ?? "" },
      })
    },
    queryKey: ["product", targetedPlan?.slug],
  })

  const getPriceObject = () => {
    if (!product.data) return null

    if (product.data.prices == null) {
      captureException(
        new Error("Product prices is null in change your plan dialog"),
        scope => {
          scope.setExtras({
            productData: product.data,
          })
          return scope
        }
      )
      return
    }

    return {
      currency: product.data.prices.currency,
      itemPrice: product.data.prices.price,
      finalAmount: product.data.prices.price,
      discountAmount: 0,
      taxes: [],
    }
  }

  const payment = useSubscriptionPayment({
    purchaseDetails: getPurchaseDetails(product.data, getPriceObject()),
    logoSrc,
    stripeConfig: {
      redirectRoute: window.location.href,
    },
    razorpayConfig: {
      onSuccess: async () => {
        setStep(ChangeSteps.paymentInProgress)
      },
      getUserDetails: () => ({}),
    },
  })

  const handleChangePlan = async () => {
    try {
      if (step === ChangeSteps.offers) {
        const response = await api.subscriptions.changePlan({
          data: {
            product_slug: targetedPlan?.slug,
          },
        })

        if (
          (subscriptionDetails?.subscription.metaData as MetaDataForPaidUser)
            .paymentMethodCode === PaymentMethodType.razorpaySubscription
        ) {
          // Close this dialog temporarily to allow Razorpay interaction
          setIsDialogMinimized(true)

          payment.changePaymentPlan({
            paymentMethod: PaymentMethodType.razorpaySubscription,
            mandateId: response[0].paymentMethodDetails.mandateLinkId,
          })
        } else {
          // For non-Razorpay payments, directly show success
          setStep(ChangeSteps.success)
        }
      }

      if (step === ChangeSteps.success) {
        handleClose()
        subscriptionDetailsQuery?.refetch()
        return
      }
    } catch (error) {
      console.log(error)
      mapErrorsV2(error)
      // Restore dialog on error
      setIsDialogMinimized(false)
    }
  }

  useEffect(() => {
    if (!open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setStep(ChangeSteps.offers)
      setIsDialogMinimized(false)
    }
  }, [open])

  useQuery({
    queryKey: ["subscription-status-check"],
    enabled:
      step === ChangeSteps.paymentInProgress &&
      !!subscriptionDetailsQuery?.refetch,
    refetchInterval: 3000,
    queryFn: async () => {
      const oldSubscriptionSlug = subscriptionDetails?.product?.slug
      const response = await subscriptionDetailsQuery?.refetch()
      if (oldSubscriptionSlug !== response?.data?.product?.slug) {
        setIsDialogMinimized(false)
        setStep(ChangeSteps.success)
      }
    },
  })

  if (step === ChangeSteps.paymentInProgress) {
    const targetElement = document.getElementById(
      PAYMENT_UNDER_PROCESS_PAGE_PORTAL
    )

    if (!targetElement) {
      throw new Error("Payment process container element not found")
    }

    return createPortal(
      <PaymentUnderProcess
        onComplete={() => {
          setStep(ChangeSteps.success)
          setIsDialogMinimized(false)
        }}
      />,
      targetElement
    )
  }

  return (
    <AdaptiveDialogAndSheet
      sheetHeight={step === ChangeSteps.offers ? 78 : 50}
      open={open && !isDialogMinimized}
      handleClose={handleClose}
      closeWhenInteractOutside
      contentClassName="sm:!w-[430px] overflow-hidden rounded-xl"
      bodyClassName={clsx(styles.body, "p-0")}
      body={
        <>
          {/* Header */}
          {step === ChangeSteps.offers && (
            <>
              <div className={clsx(styles.header, "relative py-3 pl-3 pr-2")}>
                <img
                  src={
                    mode === DialogThemeMode.dark ? darkOverlay : lightOverlay
                  }
                  alt=""
                  className="pointer-events-none absolute -top-px left-0 z-0"
                />
                <div className="relative z-2 mb-2 flex justify-between">
                  <SuraasaPlusLogo
                    variant={LogoVariant.plus}
                    height={20}
                    width={44}
                    color={styles.text.plus}
                  />
                  <IconButton
                    className={clsx("rounded-lg", styles.buttons.x)}
                    onClick={handleClose}
                  >
                    <Xmark className="!size-2.5" strokeWidth={2} />
                  </IconButton>
                </div>
                <div>
                  <Typography
                    variant="title2"
                    className={clsx("w-fit", styles.text.primary)}
                  >
                    Change your plan
                  </Typography>
                  <Typography
                    className={clsx("mt-1", styles.text.secondary)}
                    variant="body"
                  >
                    Please select a plan below
                  </Typography>
                </div>
              </div>
              <div
                className={clsx(
                  styles.body,
                  "flex flex-col gap-1.5 px-3 pb-3 pt-1"
                )}
              >
                {productDetailsQuery.isLoading ? (
                  <>
                    <div
                      className={clsx(
                        "flex animate-pulse flex-col gap-1 rounded-lg px-2 py-1.25",
                        {
                          "bg-onSurface-800": mode === DialogThemeMode.dark,
                          "border border-onSurface-200":
                            mode === DialogThemeMode.light,
                        }
                      )}
                    >
                      <div className="h-1 w-14 rounded bg-onSurface-300/30" />
                      <div className="h-2 w-20 rounded bg-onSurface-300/30" />
                    </div>

                    <div
                      className={clsx(
                        "flex animate-pulse flex-col items-start rounded-xl border-2 p-2.5",
                        styles.selectedBg
                      )}
                    >
                      <div className="mb-1 h-1.5 w-5 rounded bg-onSurface-300/30" />
                      <div className="mb-2 h-3 w-20 rounded bg-onSurface-300/30" />
                      <div className="mb-1 h-3 w-20 rounded bg-onSurface-300/30" />
                      <div className="flex gap-2">
                        <div className="h-1.5 w-10 rounded bg-onSurface-300/30" />
                        <div className="h-1.5 w-10 rounded bg-onSurface-300/30" />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Current Plan */}
                    <div
                      className={clsx(
                        "flex flex-col gap-1 rounded-lg px-2 py-1.25",
                        {
                          "bg-onSurface-800": mode === DialogThemeMode.dark,
                          "border border-onSurface-200":
                            mode === DialogThemeMode.light,
                        }
                      )}
                    >
                      <Typography
                        variant="preTitle"
                        className={styles.text.current}
                      >
                        CURRENT
                      </Typography>
                      <Typography
                        variant="strong"
                        className={styles.text.planHeading}
                      >
                        {isCurrentPlanMonthly ? "Monthly Plan" : "Annual Plan"}
                      </Typography>
                    </div>

                    {/* Target Plan */}
                    <div
                      className={clsx(
                        "flex flex-col items-start rounded-xl border-2 border-onSurface-600 p-2.5",
                        styles.selectedBg
                      )}
                    >
                      <Typography
                        variant="preTitle"
                        className="mb-1.25 text-warning-300"
                      >
                        New
                      </Typography>
                      <Typography
                        variant="title3"
                        className={clsx("mb-1.5", styles.text.planHeading)}
                      >
                        {isCurrentPlanMonthly ? "Annual Plan" : "Monthly Plan"}
                      </Typography>
                      <Typography
                        className={clsx(
                          "w-fit text-start text-title1Mobile",
                          styles.text.planPrice
                        )}
                      >
                        {currencySymbol + formatPrice(price)}
                      </Typography>
                      {savings > 0 && (
                        <div className="flex items-center gap-1">
                          <Typography className="bg-[linear-gradient(90deg,#B7B7B7_0%,#64748B_100%)] bg-clip-text text-base text-transparent line-through decoration-muted">
                            {currencySymbol + formatPrice(mrp)}
                          </Typography>
                          <Typography
                            className="text-success-400"
                            variant="strongSmallBody"
                          >
                            Save {currencySymbol + formatPrice(savings)}
                          </Typography>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </>
          )}

          {step === ChangeSteps.success && (
            <SuccessStep
              styles={styles}
              validThroughDate={subscriptionDetails?.subscription.validThrough}
              updatedPlan={subscriptionDetails?.product?.name ?? "Suraasa Plus"}
              handleClose={handleClose}
            />
          )}

          {/* Footer */}
          <div
            className={clsx(
              "flex w-full flex-col-reverse items-center justify-end gap-2 border-0 px-3 pb-3 sm:flex-row sm:gap-3",
              styles.footer,
              { "justify-center py-3": step === ChangeSteps.success }
            )}
          >
            <Button
              variant="text"
              className={clsx("w-full p-1.5 sm:w-fit", styles.buttons.text, {
                hidden: step === ChangeSteps.success,
              })}
              onClick={handleClose}
            >
              <Typography variant="strong">Cancel</Typography>
            </Button>
            <AsyncBuilder
              handler={handleChangePlan}
              render={({ onClick, loading }) => (
                <Button
                  variant="filled"
                  className={clsx(
                    "w-full rounded-xl px-2 py-1.5 font-semibold sm:w-fit",
                    styles.buttons.primary
                  )}
                  loading={loading}
                  disabled={productDetailsQuery.isLoading}
                  onClick={onClick}
                >
                  {step === ChangeSteps.success ? "Done" : "Change Plan"}
                </Button>
              )}
            />
          </div>
        </>
      }
    />
  )
}

export default ChangeYourPlanDialog
